<template>
  <v-container class="fill-height" style="opacity: 0.9" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="12" md="10">
        <v-card class="elevation-12">
          <v-toolbar color="primary" flat>
            <v-toolbar-title>{{ $t('Register Stars') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-progress-linear
              :active="loading"
              :indeterminate="true"
              absolute
              bottom
              color="white"
            ></v-progress-linear>
          </v-toolbar>
          <v-card-text>
            <v-form ref="formRegistration">
              <v-row justify="center">
                <v-col cols="6">
                  <v-text-field
                    v-model="searchKey"
                    ref="searchKey"
                    :label="$t('Search Registry Number')"
                    prepend-icon="mdi-tag-text-outline"
                    :hint="$t('Unique registry number')"
                  ></v-text-field>
                </v-col>
                <v-col cols="1">
                  <v-btn fab color="primary" @click="searchRegistry(searchKey)">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row justify="center">
                <v-col cols="6">
                  <v-menu
                    ref="dateMenu"
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="dateFormatted"
                        persistent-hint
                        :label="$t('Registration Date')"
                        prepend-icon="mdi-calendar-outline"
                        :hint="$t('Date of registration (DD.MM.YYYY)')"
                        @blur="date = parseDate(dateFormatted)"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="date"
                      color="primary"
                      no-title
                      @input="dateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="registry.registryKey"
                    :label="$t('Registry Number')"
                    prepend-icon="mdi-tag-outline"
                    :hint="$t('Unique registry number')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row align="center" justify="center">
                <v-col cols="6">
                  <v-text-field
                    v-model="registry.registerName"
                    :label="$t('Registered Name')"
                    prepend-icon="mdi-account-box-outline"
                    :hint="$t('Name of the registry holder')"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-combobox
                    clearable
                    v-model="registry.starName"
                    :items="stars"
                    item-text="nick_name"
                    item-value="short_name"
                    :return-object="false"
                    :label="$t('Registered Star')"
                    prepend-icon="mdi-star-circle-outline"
                    :hint="$t('Choose a star!')"
                  ></v-combobox>
                </v-col>
              </v-row>
              <v-select
                v-model="registry.registeredBy"
                :items="regBys"
                :label="$t('Registered By')"
                prepend-icon="mdi-city-variant-outline"
                :hint="$t('Partner name, This will be displayed in info panel')"
              ></v-select>
              <v-textarea
                v-model="registry.greetMessage"
                :label="$t('Greetings Message')"
                prepend-icon="mdi-message-text-outline"
                rows="3"
                :hint="$t('(Optional) This message will be displayed on info panel')"
              ></v-textarea>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <p>
              {{ $t('Registry Overwrite Warning') }}
            </p>
            <v-spacer></v-spacer>
            <cert-download-buttons
              v-if="certContext"
              :store="certContext.store"
              :loading="certLoading"
              @download="downloadCert"
            ></cert-download-buttons>
            <v-btn @click="delReg" color="error">{{ $t('Delete') }}</v-btn>
            <v-btn @click="registerStar" color="primary">{{ $t('Register') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog.show" width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ dialog.title }}</span>
        </v-card-title>
        <v-card-text v-if="dialog.message">
          <registry-info-card :data="[dialog.message]"></registry-info-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <cert-download-buttons
            v-if="dialog.message && certContext"
            :store="certContext.store"
            :loading="certLoading"
            @download="downloadCert"
          ></cert-download-buttons>
          <v-btn
            v-if="dialog.message"
            color="primary darken-1"
            text
            :href="'https://software.space-registry.org/registry/' + dialog.message.registryKey"
            target="_blank"
          >
            {{ $t('View In Software') }}
          </v-btn>
          <v-btn color="primary darken-1" text @click="dialog.show = false"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import swh from '@/assets/js/sw_helper'
  import { deleteRegistry, getRegistry, saveRegistry } from '../services/registryService'
  import { getSkySourceByName, getSkySources } from '../services/skySourceService'
  import { getStoresProtected } from '../services/storeService'
  import {
    createCert,
    createCertAmz,
    createCustomCert,
  } from '../services/pdfGenerationService'
  import api from '@/assets/js/api_helper'
  import RegistryInfoCard from '../components/RegistryInfoCard.vue'
  import CertDownloadButtons from '../components/CertDownloadButtons.vue'

  const AMZ_KEY_REGEX = /^IS25-\d+-[A-Z]{2,3}$/
  export default {
    props: {
      source: String,
    },
    data() {
      return {
        registry: {
          registryKey: '',
          registerName: undefined,
          greetMessage: undefined,
          registeredBy: '',
          registerDate: '',
          starName: null,
        },
        dateMenu: false,
        date: new Date().toISOString().substring(0, 10),
        dateFormatted: swh.formatDate(new Date()),
        stars: [],
        regBys: [],
        storeList: [],
        searchKey: '',
        dialog: {
          show: false,
          title: '',
          messages: '',
          message: '',
        },
        loading: false,
        certLoading: null,
        // Registry the cert buttons act on: set by a successful search or
        // register, so editing the form fields doesn't change the target.
        certRegistry: null,
        apiUrl: `${process.env.VUE_APP_YOUVENDO_API_SERVER}/`,
      }
    },
    computed: {
      // Where certificates for the current registry can be generated:
      // Amazon keys carry everything server-side (/certs/amazon), Shopify
      // registries need the originating order + store (/certs/new). Manually
      // created registries have neither, so they go through /certs/custom
      // (same as the Cert Generator) built from the registry's own data.
      certContext() {
        const reg = this.certRegistry
        const key = (reg?.registryKey || '').toUpperCase()
        if (!key) return null

        const findStore = (id) =>
          id && this.storeList.find((s) => s.id.toUpperCase() === id.toUpperCase())
        const domainStore = this.storeList.find((s) => s.domain === reg.registeredBy)

        if (AMZ_KEY_REGEX.test(key)) {
          // IS25-<n>-<STORE>: suffix names the Amazon store, used for colours
          const store = domainStore || findStore(key.split('-').pop()) || null
          return { type: 'amazon', key, store }
        }

        const orderId = reg.metadata?.orderId
        if (orderId && domainStore) {
          return { type: 'shopify', orderId, storeId: domainStore.id, store: domainStore }
        }

        // /certs/custom needs a real colour id, so drop the order-based "Default"
        const store = domainStore || this.storeList[0]
        if (!store) return null
        return {
          type: 'custom',
          registry: reg,
          lang: store.lang,
          store: { ...store, colors: (store.colors || []).filter((c) => c.id) },
        }
      },
    },
    watch: {
      date() {
        this.dateFormatted = swh.formatDate(this.date)
        this.registry.registerDate = swh.formatDate(this.date)
      },
    },
    methods: {
      parseDate(dateString) {
        return swh.parseDate(dateString)
      },
      async delReg() {
        try {
          await deleteRegistry(this.registry.registryKey)
        } catch (error) {
          console.log(error)
        }
      },
      async registerStar() {
        try {
          this.loading = true
          const { data } = await saveRegistry(this.registry)
          this.$store.commit('showSnackbar', {
            message: 'Star registered successfully!',
            color: 'success',
          })

          this.certRegistry = { ...this.registry, ...data }
          this.showDialog(data.registryKey, data)
        } catch (error) {
          if (error.response) {
            this.$store.commit('showSnackbar', {
              message: `Unable to register: ${error.response.data?.message ?? error.response.data}`,
              color: 'error',
            })
          }
        } finally {
          this.loading = false
        }
      },

      async searchRegistry(searchKey) {
        if (!searchKey) {
          this.$refs.searchKey.focus()
          return
        }
        try {
          this.loading = true
          this.certRegistry = null
          const { data } = await getRegistry(searchKey.toUpperCase())
          console.log(data)
          this.date = new Date(data.originalDate._seconds * 1000).toISOString().substring(0, 10)
          this.registry = data
          this.certRegistry = data
          const found = this.stars.findIndex((s) => s.short_name === data.starName)
          if (found === -1) {
            this.stars.push({
              short_name: data.starName,
              nick_name: data.starName,
            })
          }
          this.star = data.starName
        } catch (error) {
          if (error.response) {
            this.$store.commit('showSnackbar', {
              message: this.$t(error.response.data),
              color: 'error',
            })
          }
        } finally {
          this.loading = false
        }
      },

      showDialog(title, message) {
        this.dialog.message = message

        this.dialog.title = title
        this.dialog.show = true
      },

      // Same payload shape the Cert Generator (ManageCerts) posts to /certs/custom
      async buildCustomCertData(reg) {
        const star = await this.findStar(reg.starName)
        if (!star?.coords) throw Error(`No coordinates found for star ${reg.starName}`)

        const parse = (c) => {
          const [lat, long] = c.split('  ')
          return { lat: lat.trim(), long: long.trim() }
        }
        const date = reg.originalDate?._seconds
          ? new Date(reg.originalDate._seconds * 1000)
          : new Date(swh.parseDate(reg.registerDate))

        return {
          registerName: reg.registerName,
          // /certs/custom parses DD.MM.YYYY only (same as the Cert Generator's picker)
          registerDate: swh.formatDate(date),
          registryKey: reg.registryKey,
          registeredBy: reg.registeredBy,
          register: false,
          coords: parse(star.coords),
          coords_2: star.coords2 ? parse(star.coords2) : { lat: '', long: '' },
          starName: star.name || reg.starName,
          variant: 'single',
          brightness: 'low',
          format: this.$t('Standard'),
        }
      },

      async findStar(starName) {
        if (!starName) return null
        const matches = (s) => [s.short_name, s.name, s.nick_name].includes(starName)

        const local = this.stars.find(matches)
        if (local?.coords) return local

        const { data } = await api.getStarCatalogue({ search: starName, limit: 50 })
        const fromCatalogue = (data.items || []).find(matches)
        if (fromCatalogue) return fromCatalogue

        // The catalogue only lists assignable brightness tiers; bright named
        // stars (e.g. Zuben Elakrab) resolve through the general name lookup.
        try {
          const { data: star } = await getSkySourceByName(starName)
          return star
        } catch (error) {
          return null
        }
      },

      async downloadCert(version, color) {
        const ctx = this.certContext
        if (!ctx) return

        try {
          this.certLoading = version
          let urls = []

          if (ctx.type === 'amazon') {
            // /certs/amazon resolves name, date, coords and metadata from Mongo
            const { data } = await createCertAmz({
              registries: [{ registryKey: ctx.key }],
              id: ctx.key,
              color: color?.id,
              store: ctx.store?.id,
              version,
            })
            urls = data
          } else if (ctx.type === 'custom') {
            if (!color?.id) throw Error('Pick a colour')
            const { data } = await createCustomCert(
              await this.buildCustomCertData(ctx.registry),
              color.id,
              version,
              ctx.lang
            )
            // { files, registered } — registering is reported separately; the files are what we open.
            urls = data.files.map((item) => (typeof item === 'string' ? this.apiUrl + item : item.url))
          } else {
            const { data } = await createCert({
              orderIds: [ctx.orderId],
              color: color?.id,
              store: ctx.storeId,
              version,
            })
            urls = data.map((item) => (typeof item === 'string' ? this.apiUrl + item : item.url))
          }

          urls.forEach((url) => window.open(url, '_blank'))
        } catch (error) {
          console.log(error)
          this.$store.commit('showSnackbar', {
            message: error.response ? 'Could not generate certificate!' : error.message,
            color: 'error',
          })
        } finally {
          this.certLoading = null
        }
      },
    },
    mounted: async function () {
      this.storeList = await getStoresProtected()
      this.regBys = this.storeList.map((s) => s.domain)
      this.regBy = this.regBys.length > 0 ? this.regBys[0] : ''

      const { data } = await getSkySources(true)
      this.stars = data.filter((sso) => sso.checked === true)
      console.log('stars', this.stars)
    },
    components: { RegistryInfoCard, CertDownloadButtons },
  }
</script>

<style></style>
